import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Trash2,
  UserCheck,
  UserMinus,
  UserRoundCog,
  UserX,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { formatDateTime } from "../lib/format";
import { session as authSession } from "../../auth";

type AccessStatus = "pending" | "approved" | "declined" | "suspended";
type MemberCohort = "founding" | "beta" | "standard";

type AccountRow = {
  id: number;
  name: string;
  email: string;
  access_status: AccessStatus;
  member_cohort: MemberCohort;
  created_at: string | null;
  access_status_updated_at: string | null;
  email_verified: boolean | null;
  linked_at: string | null;
};

type CohortRow = {
  id: number | string;
  member_cohort: MemberCohort;
};

const STATUS_LABELS: Record<AccessStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  declined: "Declined",
  suspended: "Suspended",
};

const COHORT_LABELS: Record<MemberCohort, string> = {
  founding: "Founding Member",
  beta: "Beta Member",
  standard: "Member",
};

export default function AdminAccountsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<"all" | AccessStatus>("all");
  const [message, setMessage] = useState("");

  const currentUserQuery = useQuery({
    queryKey: ["session"],
    queryFn: () => authSession.get(),
  });

  const {
    data: accounts = [],
    isLoading,
    error,
    refetch,
  } = useQuery<AccountRow[]>({
    queryKey: ["admin_accounts"],
    queryFn: async () => {
      const [accountsResponse, cohortsResponse] = await Promise.all([
        fetch("/app-api/admin/accounts"),
        fetch("/app-api/admin/member-cohorts", { cache: "no-store" }),
      ]);
      if (!accountsResponse.ok || !cohortsResponse.ok)
        throw new Error("Forbidden or unavailable");

      const accountRows = await accountsResponse.json();
      const cohortRows = (await cohortsResponse.json()) as CohortRow[];
      const cohortById = new Map(
        cohortRows.map((row) => [String(row.id), row.member_cohort]),
      );

      return accountRows.map((account: Omit<AccountRow, "member_cohort">) => ({
        ...account,
        member_cohort: cohortById.get(String(account.id)) ?? "standard",
      }));
    },
  });

  const mutation = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: number;
      status: AccessStatus;
    }) => {
      const response = await fetch(`/app-api/admin/accounts/${id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(payload?.error || "Could not update account");
      return payload;
    },
    onSuccess: (_data, variables) => {
      setMessage(`Account set to ${STATUS_LABELS[variables.status].toLowerCase()}.`);
      queryClient.invalidateQueries({ queryKey: ["admin_accounts"] });
    },
    onError: (err) => {
      setMessage(err instanceof Error ? err.message : "Could not update account");
    },
  });

  const cohortMutation = useMutation({
    mutationFn: async ({
      id,
      memberCohort,
    }: {
      id: number;
      memberCohort: MemberCohort;
    }) => {
      const response = await fetch(`/app-api/admin/accounts/${id}/cohort`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ member_cohort: memberCohort }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(payload?.error || "Could not update member type");
      return payload;
    },
    onSuccess: (_data, variables) => {
      setMessage(`Member type set to ${COHORT_LABELS[variables.memberCohort]}.`);
      queryClient.invalidateQueries({ queryKey: ["admin_accounts"] });
      queryClient.invalidateQueries({ queryKey: ["member_profile"] });
    },
    onError: (err) => {
      setMessage(err instanceof Error ? err.message : "Could not update member type");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({
      id,
      confirmEmail,
    }: {
      id: number;
      confirmEmail: string;
    }) => {
      const response = await fetch(`/app-api/admin/accounts/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm_email: confirmEmail }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(payload?.error || "Could not delete account");
      return payload;
    },
    onSuccess: () => {
      setMessage("Account and personal CatchCompass data permanently deleted.");
      queryClient.invalidateQueries({ queryKey: ["admin_accounts"] });
    },
    onError: (err) => {
      setMessage(err instanceof Error ? err.message : "Could not delete account");
    },
  });

  function deleteAccount(account: AccountRow) {
    if (typeof window === "undefined") return;
    const typed = window.prompt(
      `Permanent deletion cannot be undone. Type ${account.email} exactly to continue.`,
    );
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== account.email.trim().toLowerCase()) {
      setMessage("Email confirmation did not match. Nothing was deleted.");
      return;
    }
    if (
      !window.confirm(
        `Permanently delete ${account.name || account.email} and their personal CatchCompass data?`,
      )
    )
      return;

    setMessage("");
    deleteMutation.mutate({ id: account.id, confirmEmail: typed.trim() });
  }

  const counts = useMemo(() => {
    const next = { pending: 0, approved: 0, declined: 0, suspended: 0 };
    for (const account of accounts) next[account.access_status] += 1;
    return next;
  }, [accounts]);

  const shown = useMemo(
    () =>
      filter === "all"
        ? accounts
        : accounts.filter((account) => account.access_status === filter),
    [accounts, filter],
  );

  function setStatus(account: AccountRow, status: AccessStatus) {
    const verb =
      status === "approved"
        ? "approve"
        : status === "declined"
          ? "decline"
          : status === "suspended"
            ? "suspend"
            : "return to pending";
    if (
      typeof window !== "undefined" &&
      !window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${account.name || account.email}?`)
    )
      return;
    setMessage("");
    mutation.mutate({ id: account.id, status });
  }

  function setMemberCohort(account: AccountRow, memberCohort: MemberCohort) {
    if (memberCohort === account.member_cohort) return;
    setMessage("");
    cohortMutation.mutate({ id: account.id, memberCohort });
  }

  const actionsPending =
    mutation.isPending || cohortMutation.isPending || deleteMutation.isPending;

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link to="/admin" className="text-sm font-medium text-accent hover:underline">
          Admin
        </Link>
        <div className="mt-2 flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset text-secondary">
            <UserRoundCog />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-primary">
              Account Access
            </h1>
            <p className="mt-1 text-sm text-secondary">
              Approve new anglers, set their member type, decline access requests, or suspend an existing account.
            </p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Summary label="Pending" value={counts.pending} />
        <Summary label="Approved" value={counts.approved} />
        <Summary label="Suspended" value={counts.suspended} />
        <Summary label="Declined" value={counts.declined} />
      </div>

      <div className="flex flex-wrap gap-2">
        {(["all", "pending", "approved", "suspended", "declined"] as const).map(
          (status) => (
            <Button
              key={status}
              type="button"
              variant={filter === status ? "primary" : "secondary"}
              size="sm"
              onClick={() => setFilter(status)}
            >
              {status === "all" ? "All" : STATUS_LABELS[status]}
            </Button>
          ),
        )}
      </div>

      {message ? (
        <div className="rounded-lg border border-border bg-inset p-3 text-sm text-secondary">
          {message}
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-lg border border-border bg-raised p-6 text-center text-sm text-secondary">
          Loading accounts…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-error bg-error-weak p-4 text-sm text-error">
          Could not load accounts.{" "}
          <Button variant="tertiary" size="sm" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-secondary">
          No accounts in this group.
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((account) => (
            <article
              key={account.id}
              className="rounded-xl border border-border bg-raised p-4"
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold text-primary">
                      {account.name || "Unnamed angler"}
                    </h2>
                    <span className="rounded-full bg-inset px-2 py-0.5 text-xs font-semibold text-secondary">
                      {STATUS_LABELS[account.access_status]}
                    </span>
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${account.member_cohort === "founding" ? "border-accent/50 bg-accent/10 text-accent" : "border-border bg-background text-secondary"}`}>
                      {COHORT_LABELS[account.member_cohort]}
                    </span>
                    {account.email_verified ? (
                      <span className="inline-flex items-center gap-1 text-xs text-success">
                        <ShieldCheck className="size-3.5" /> Email verified
                      </span>
                    ) : (
                      <span className="text-xs text-secondary">Email not verified</span>
                    )}
                  </div>
                  <p className="mt-1 break-all text-sm text-secondary">{account.email}</p>
                  <p className="mt-2 text-xs text-secondary">
                    Linked {account.linked_at ? formatDateTime(account.linked_at) : "Not recorded"}
                    {account.access_status_updated_at
                      ? ` · Access updated ${formatDateTime(account.access_status_updated_at)}`
                      : ""}
                  </p>
                </div>

                <div className="flex flex-col gap-2 md:items-end">
                  <label className="text-xs font-semibold text-secondary">
                    Member type
                    <select
                      className="ml-2 rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm font-medium text-primary"
                      value={account.member_cohort}
                      onChange={(event) =>
                        setMemberCohort(
                          account,
                          event.target.value as MemberCohort,
                        )
                      }
                      disabled={actionsPending}
                    >
                      <option value="founding">Founding Member</option>
                      <option value="beta">Beta Member</option>
                      <option value="standard">Member</option>
                    </select>
                  </label>

                  <div className="flex flex-wrap gap-2 md:justify-end">
                    {account.access_status !== "approved" ? (
                      <Button
                        size="sm"
                        onClick={() => setStatus(account, "approved")}
                        disabled={actionsPending}
                      >
                        <UserCheck className="size-4" /> Approve
                      </Button>
                    ) : null}
                    {account.access_status !== "suspended" ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setStatus(account, "suspended")}
                        disabled={actionsPending}
                      >
                        <UserMinus className="size-4" /> Suspend
                      </Button>
                    ) : null}
                    {account.access_status !== "declined" ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setStatus(account, "declined")}
                        disabled={actionsPending}
                      >
                        <UserX className="size-4" /> Decline
                      </Button>
                    ) : null}
                    {currentUserQuery.data?.email?.toLowerCase() ===
                    account.email.toLowerCase() ? (
                      <span className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-secondary">
                        <ShieldCheck className="size-3.5" /> Protected admin
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary-destructive"
                        onClick={() => deleteAccount(account)}
                        disabled={actionsPending}
                      >
                        <Trash2 className="size-4" /> Delete permanently
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-raised p-3">
      <p className="text-xs font-medium text-secondary">{label}</p>
      <p className="mt-1 text-xl font-semibold text-primary">{value}</p>
    </div>
  );
}
